// A ride interrupted by a page closed, reloaded or crashed (ADR 0028), picked up again when the app opens: back in the
// online room while it holds the seat, or the ride the server kept, to continue, save or drop. The server is the source
// of truth: the recording it holds is the ride; this tab only remembers its room seat (net/client.ts).
import type { OpenRecording, OpenRecordingResponse } from '@gruvetto/contracts';
import { api } from '../data/api.js';
import { adopt } from '../data/activities.js';
import type { SaveResponse } from '../data/recordings.js';
import { verdict, FRESH_MS } from '../data/live-ride.js';
import { catalogReady } from '../data/creations.js';
import { state } from '../data/store.js';
import { net, seatStore } from '../net/client.js';
import { modal, esc, on, toast, fmtDur, fmtKm } from './dom.js';
import { withIcon } from './brand.js';
import { navigate, refresh } from './router.js';
import { startRide, isRiding } from './ride/start.js';
import { canResume, configOf, lastDistance } from './ride/resumable.js';
import { t } from '../i18n/index.js';
import { errorText } from '../i18n/content.js';

/** Shorter than this, a recording is not worth a question: the server drops it when it settles (RECORDING_MIN_S). */
const MIN_S = 30;
/** How long a rejoined room has to restart our race before the recording is offered as an interrupted ride. */
const START_WAIT_MS = 2500;

let offered = false, retried = false;
/** Signed out: the next account is asked again, from scratch. */
export const forgetResume = () => { offered = false; retried = false; };

/** The recording `id` of this tab's online race, when the server still holds it open: a rejoined race goes on with it. */
export async function onlineRecording(id: string): Promise<OpenRecording | null> {
  try {
    const rec = (await api<OpenRecordingResponse>('/recordings/open')).recording;
    return rec && rec.id === id && rec.meta.online ? rec : null;
  } catch { return null; }
}

/** Once per page and account, after the terms are accepted (main.ts). */
export async function offerResume(): Promise<void> {
  if (offered || isRiding()) return;
  offered = true;
  let rec = await openRecording();
  const seat = seatStore.get();
  // this tab's own online race goes on with the room (the race launch takes it), whatever the beats say
  let mine: OpenRecording | null = null;
  if (seat && rec?.meta.online && rec.id === seat.rec) { mine = rec; rec = null; }
  if (rec) {
    const v = verdict(rec);
    // a page that crashed beats no more but has not said so: ask again once its last beat is old
    if (v === 'wait' && !retried) { retried = true; setTimeout(() => void askAgain(), FRESH_MS + 1000); }
    if (v !== 'offer') rec = null;
  }
  if (seat) {
    const back = await rejoinRoom();
    // the room no longer runs our race (over, or it went on without us): the ride is an interrupted one
    if (back) await new Promise(r => setTimeout(r, START_WAIT_MS));
    if (!isRiding()) rec ??= mine;
  }
  if (rec && !isRiding()) await ask(rec);
}

/** The latest recording left open, when it is long enough to be worth a question; null offline. */
async function openRecording(): Promise<OpenRecording | null> {
  try {
    const rec = (await api<OpenRecordingResponse>('/recordings/open')).recording;
    return rec && rec.samples.t.length >= MIN_S ? rec : null;
  } catch { return null; /* offline: the server settles the ride later */ }
}

/** The second look at a ride another page still seemed to ride: offered now if that page has gone quiet. */
async function askAgain() {
  if (isRiding() || !offered) return;
  const rec = await openRecording();
  if (rec && verdict(rec) === 'offer' && !isRiding()) await ask(rec);
}

/** Back in the room this tab had a seat in; the room screen launches the race if it still runs (its `start` listener). */
async function rejoinRoom(): Promise<boolean> {
  const R = t.resume;
  await import('./screens/online.js');
  toast(R.rejoining);
  const back = await net.rejoin();
  if (!back) { toast(R.rejoinFailed, 'bad'); return false; }
  toast(R.rejoined, 'good');
  navigate('/online');
  return true;
}

const ago = (ms: number) => {
  const R = t.resume, min = Math.floor(Math.max(0, ms) / 60_000);
  return min < 1 ? R.justNow : min < 60 ? R.minutesAgo(min) : R.hoursAgo(Math.floor(min / 60));
};

/** The dialog: what was ridden, then continue (when the ride can go on), save, or discard after a confirmation. */
async function ask(rec: OpenRecording): Promise<void> {
  await catalogReady.catch(() => {});
  const R = t.resume, resumable = canResume(rec), workout = rec.setup?.kind === 'workout' ? rec.setup.workout : null;
  const secs = rec.samples.t.length, help = !resumable ? (rec.meta.online ? R.helpOnline : R.helpRace) : workout ? R.helpWorkout : R.helpResume;
  const tile = (v: string, k: string) => `<div class="tile"><div class="v">${esc(v)}</div><div class="k">${esc(k)}</div></div>`;
  const m = modal(`<h2>${esc(resumable ? R.title : R.titleOver)}</h2>
    <p class="mut">${esc(rec.meta.name)}</p>
    <div class="grid g3 my-3">${tile(fmtDur(secs), R.kRidden)}${tile(fmtKm(lastDistance(rec.samples)), R.kDistance)}${tile(ago(Date.now() - rec.updatedAt), R.kStopped)}</div>
    ${workout ? '<canvas class="chart" data-resume-chart></canvas>' : ''}
    <p class="mut small mt-2">${esc(help)}</p>
    <p class="mut small">${esc(R.helpLater)}</p>
    <div class="actions"><button class="ghost" data-act="discard">${esc(R.discard)}</button><button ${resumable ? '' : 'class="primary"'} data-act="save">${esc(R.save)}</button>${resumable ? `<button class="primary" data-act="resume">${withIcon('play', R.resume, 16)}</button>` : ''}</div>`);
  if (workout) {
    const { workoutProfile } = await import('./components/charts.js');
    requestAnimationFrame(() => workoutProfile(m.el.querySelector('[data-resume-chart]'), workout, state.profile.ftp, { h: 80, progress: secs }));
  }
  on(m.el, 'click', '[data-act="resume"]', async () => {
    const cfg = configOf(rec.setup);
    if (!cfg) return;
    m.close();
    try { await startRide({ ...cfg, resume: rec }); } catch (e) { toast(errorText((e as Error).message), 'bad'); }
  });
  on(m.el, 'click', '[data-act="save"]', async () => {
    try {
      const r = await api<SaveResponse>(`/recordings/${rec.id}/close`, { method: 'POST', body: { meta: rec.meta } });
      const saved = await adopt(r, rec.id, rec.samples);
      m.close();
      toast(R.saved(saved.gained), 'good');
      refresh();
    } catch (e) { toast(errorText((e as Error).message) || R.failed, 'bad'); }
  });
  on(m.el, 'click', '[data-act="discard"]', () => {
    const c = modal(`<h2>${esc(R.discardTitle)}</h2><p class="mut">${esc(R.discardHelp)}</p>
      <div class="actions"><button data-close>${esc(R.keep)}</button><button class="danger" data-act="discard-yes">${esc(R.discardYes)}</button></div>`);
    on(c.el, 'click', '[data-act="discard-yes"]', async () => {
      try {
        await api(`/recordings/${rec.id}`, { method: 'DELETE' });
        c.close(); m.close();
        toast(R.discarded);
      } catch (e) { toast(errorText((e as Error).message) || R.failed, 'bad'); }
    });
  });
}
